import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Plus, Terminal, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDelete, apiGet, apiPost } from '../lib/api';
import ConfirmDialog from './ui/ConfirmDialog';
import OneTimeKeyNotice from './ui/OneTimeKeyNotice';

interface MyKey {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  jobCount: number;
}

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : 'never');

const API_BASE = `${window.location.origin}/api/v1`;

// The signed-in user's own keys for the Hinglish Subs API (API Settings).
const MyApiKeys: React.FC = () => {
  const [keys, setKeys] = useState<MyKey[]>([]);
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<{ key: string; name: string } | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<MyKey | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiGet<{ keys: MyKey[]; limit: number }>('/api-keys');
      setKeys(res.keys);
      setLimit(res.limit);
    } catch (error) {
      console.error('Error loading API keys:', error);
      toast.error('Failed to load your API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const active = keys.filter((k) => !k.revokedAt && !(k.expiresAt && new Date(k.expiresAt) < new Date()));
  const atLimit = active.length >= limit;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const created = await apiPost<{ key: string; name: string }>('/api-keys', { name: name.trim() || 'API key' });
      setNewKey({ key: created.key, name: created.name });
      setName('');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: MyKey) => {
    try {
      await apiDelete(`/api-keys/${key.id}`);
      toast.success(`Revoked "${key.name}"`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to revoke key');
    } finally {
      setPendingRevoke(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Terminal size={18} /> Hinglish Subs API keys
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Use the Hinglish Subs tab from scripts and tools: send audio or video, get a Hinglish SRT back. Results also
            appear in your History.
          </p>
        </div>
        <a
          href="/api/v1/docs"
          target="_blank"
          rel="noreferrer"
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <BookOpen size={16} />
          Docs
        </a>
      </div>

      {newKey && <OneTimeKeyNotice name={newKey.name} secret={newKey.key} onDismiss={() => setNewKey(null)} />}

      <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2">
        <label htmlFor="new-api-key-name" className="sr-only">
          Key name
        </label>
        <input
          id="new-api-key-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Key name, e.g. laptop script or n8n"
          maxLength={100}
          disabled={creating || atLimit}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={creating || atLimit}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={18} />
          {creating ? 'Creating…' : 'Create key'}
        </button>
      </form>
      {atLimit && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          You have {active.length} active keys, the maximum. Revoke one to create another.
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">You don't have any API keys yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-slate-700 border border-gray-200 dark:border-slate-700 rounded-lg">
          {[...active, ...keys.filter((k) => !active.includes(k))].map((k) => {
            const inactive = !active.includes(k);
            return (
              <li key={k.id} className={`flex items-center justify-between gap-3 px-3 py-2 ${inactive ? 'opacity-50' : ''}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {k.name}
                    {k.revokedAt ? (
                      <span className="ml-2 text-xs text-red-500">revoked</span>
                    ) : inactive ? (
                      <span className="ml-2 text-xs text-amber-600">expired</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-mono">{k.keyPrefix}…</span> · created {fmtDate(k.createdAt)} · last used{' '}
                    {fmtDate(k.lastUsedAt)} · {k.jobCount} {k.jobCount === 1 ? 'job' : 'jobs'}
                  </p>
                </div>
                {!inactive && (
                  <button
                    type="button"
                    aria-label={`Revoke ${k.name}`}
                    title="Revoke key"
                    onClick={() => setPendingRevoke(k)}
                    className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p>
          Send the key as <code className="font-mono">Authorization: Bearer tss_…</code> to{' '}
          <code className="font-mono break-all">{API_BASE}/hinglish-subs</code>.
        </p>
        <p>Keep keys secret: anyone with one can run jobs as you. Revoke a key you've lost and create a new one.</p>
      </div>

      <ConfirmDialog
        open={!!pendingRevoke}
        title="Revoke API key?"
        description={`"${pendingRevoke?.name}" (${pendingRevoke?.keyPrefix}…) will stop working immediately. This can't be undone.`}
        confirmLabel="Revoke"
        onConfirm={() => pendingRevoke && handleRevoke(pendingRevoke)}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
};

export default MyApiKeys;
